import { lazy, type ComponentType } from 'react'

const RELOAD_FLAG = 'inmeet:chunk-reload'

/**
 * Drop-in replacement for `React.lazy` that recovers from a failed dynamic
 * import.
 *
 * When a new build is deployed, Vite regenerates every hashed chunk filename
 * and the previous files are removed. A tab that still holds the *old*
 * `index-*.js` will then try to `import()` a chunk name that no longer exists
 * and fail with "Failed to fetch dynamically imported module" (a 404). Rather
 * than surfacing that as an error boundary, we do a one-time full page reload
 * so the tab picks up the fresh `index` + chunk names.
 *
 * A sessionStorage guard prevents an infinite reload loop: we only auto-reload
 * once per failure streak, and clear the guard on the next successful import.
 * If the import still fails after a reload (genuine network outage or a truly
 * missing chunk), the error propagates normally.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG)
      } catch {
        // sessionStorage may be unavailable (private mode) — ignore.
      }
      return mod
    } catch (err) {
      let alreadyReloaded = false
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === '1'
      } catch {
        // ignore
      }
      if (!alreadyReloaded) {
        try {
          window.sessionStorage.setItem(RELOAD_FLAG, '1')
        } catch {
          // ignore
        }
        window.location.reload()
        // Never resolve — keep the Suspense fallback visible until the reload
        // navigates away, so the failed module never renders.
        return new Promise<{ default: T }>(() => {})
      }
      throw err
    }
  })
}
